/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\SmsProviderCredentialsBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX SMS Messaging module.
----------------------------------------------------------------*/


import { Key } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { SmsProviderFormData } from '../types';

interface FieldConfig { required: string[]; optional: string[]; }

interface Props {
    formData: SmsProviderFormData;
    errors: Record<string, string>;
    fieldConfig: FieldConfig;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFieldChange: (field: keyof SmsProviderFormData, value: any) => void;
}

type CredField = {
    key: keyof SmsProviderFormData;
    labelKey: string;
    placeholderKey: string;
    type?: string;
};

const CRED_FIELDS: CredField[] = [
    { key: 'accountSid', labelKey: 'sms.providers.fields.accountSid', placeholderKey: 'sms.providers.fields.accountSidPlaceholder' },
    { key: 'apiKey',     labelKey: 'sms.providers.fields.apiKey',     placeholderKey: 'sms.providers.fields.apiKeyPlaceholder',     type: 'password' },
    { key: 'apiSecret',  labelKey: 'sms.providers.fields.apiSecret',  placeholderKey: 'sms.providers.fields.apiSecretPlaceholder',  type: 'password' },
    { key: 'apiEndpoint',labelKey: 'sms.providers.fields.apiEndpoint',placeholderKey: 'sms.providers.fields.apiEndpointPlaceholder' },
];

export function SmsProviderCredentialsBox({ formData, errors, fieldConfig, onFieldChange }: Props) {
    const { t } = useI18n();
    const show = (f: string) => fieldConfig.required.includes(f) || fieldConfig.optional.includes(f);
    const req  = (f: string) => fieldConfig.required.includes(f);

    return (
        <FormBox title={t('sms.providers.credentials')} icon={<Key className="h-5 w-5" />} variant="elevated">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                {CRED_FIELDS.filter((f) => show(f.key as string)).map((f) => (
                    <>
                        <SimpleTooltip key={`tt-${f.key}`} content={t(f.labelKey)} position="top">
                            <Label htmlFor={f.key as string} required={req(f.key as string)} className="text-left">
                                {t(f.labelKey)}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            key={`in-${f.key}`}
                            id={f.key as string}
                            type={f.type}
                            value={(formData[f.key] as string) || ''}
                            onChange={(e) => onFieldChange(f.key, e.target.value)}
                            placeholder={t(f.placeholderKey)}
                            error={errors[f.key as string]}
                        />
                    </>
                ))}
                {/* Fill empty cells to maintain 4-column alignment */}
                {!show('accountSid') && !show('apiSecret') && <><div /><div /></>}
            </div>
        </FormBox>
    );
}
